import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Time.css";

interface TimeItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Time: React.FC = () => {
  const [data, setData] = useState<TimeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/time`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching time:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: TimeItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: TimeItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Time",
    de: "Uhrzeit",
    ta: "நேரம்",
    si: "වේලාව",
    hi: "समय",
    ar: "الوقت",
    tr: "Zaman",
    ru: "Время",
    zh: "时间",
    fr: "Temps",
    es: "Tiempo",
  };

  const subtitleMap = {
    en: "Telling Time in German",
    de: "Uhrzeit auf Deutsch",
    ta: "ஜெர்மன் மொழியில் நேரம்",
    si: "ජර්මන් භාෂාවේ වේලාව",
    hi: "जर्मन में समय",
    ar: "الوقت بالألمانية",
    tr: "Almanca'da Zaman",
    ru: "Время на немецком",
    zh: "德语中的时间",
    fr: "Temps en allemand",
    es: "Tiempo en alemán",
  };

  return (
    <div className="time-page-container">
      <div className="time-page-header">
        <div>
          <h1 className="time-page-title">{titleMap["de"]}</h1>
          <p className="time-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="time-page-loading">Loading...</div>
      ) : (
        <div className="time-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`time-page-card ${
                  isPlaying ? "time-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="time-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="time-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="time-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Time;
